import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { Users, BookMarked, ShieldCheck, ArrowRight, Inbox, ShoppingCart, ReceiptText, MessageSquare, Wallet } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const AdminCard = ({ icon: Icon, title, description, testid, to, disabled, navigate, badge }) => (
  <button
    data-testid={testid}
    onClick={() => !disabled && navigate(to)}
    disabled={disabled}
    className={
      "text-left bg-white border border-brand-line rounded-sm p-6 transition-[box-shadow,border-color] duration-200 " +
      (disabled
        ? "opacity-70 cursor-not-allowed"
        : "hover:border-brand-blue/40 hover:shadow-md")
    }
  >
    <div className="flex items-start justify-between mb-3">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue">
        <Icon size={20} strokeWidth={1.75} />
      </div>
      {badge != null && badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="text-[10px] uppercase tracking-widest text-white bg-brand-gold rounded-sm px-2 py-0.5 font-semibold"
        >
          {badge} nuove
        </span>
      )}
      {disabled && (
        <span className="text-[10px] uppercase tracking-widest text-brand-muted bg-brand-bg border border-brand-line rounded-sm px-2 py-0.5">
          Prossima fase
        </span>
      )}
    </div>
    <h3 className="text-lg text-brand-blue mb-1">{title}</h3>
    <p className="text-sm text-brand-muted leading-relaxed mb-4">{description}</p>
    {!disabled && (
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
        Vai <ArrowRight size={14} />
      </span>
    )}
  </button>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newContacts, setNewContacts] = useState(0);

  useEffect(() => {
    api
      .get("/admin/contact-requests", { params: { status: "new", limit: 1 } })
      .then((r) => setNewContacts(r.data?.new_count || 0))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <ShieldCheck size={14} /> Pannello Super Admin
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-2">
            Benvenuta, {user?.first_name}
          </h1>
          <p className="text-brand-muted max-w-2xl">
            Amministra utenti, corsi, iscrizioni, certificati e richieste di contatto
            provenienti dal catalogo pubblico.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <AdminCard
            testid="admin-card-utenti"
            icon={Users}
            title="Utenti e Aziende"
            description="Rubrica unica di aziende, discenti e privati: gestisci account, dati e assistenza."
            to="/admin/utenti"
            navigate={navigate}
          />
          <AdminCard
            testid="admin-card-corsi"
            icon={BookMarked}
            title="Gestione Corsi"
            description="Visualizza tutti i corsi della piattaforma, indipendentemente dal formatore."
            to="/admin/corsi"
            navigate={navigate}
          />
          <AdminCard
            testid="admin-card-certificati"
            icon={ShieldCheck}
            title="Certificati"
            description="Gestisci i certificati emessi: visualizza, scarica, revoca, riabilita o forza l'emissione."
            to="/admin/certificati"
            navigate={navigate}
          />
          <AdminCard
            testid="admin-card-feedback"
            icon={MessageSquare}
            title="Feedback corsi"
            description="Gradimento anonimo raccolto dagli studenti, corso per corso, per valutarne la qualità."
            to="/admin/feedback"
            navigate={navigate}
          />
          <AdminCard
            testid="admin-card-richieste"
            icon={Inbox}
            title="Richieste contatto"
            description="Richieste dal form pubblico del catalogo. Gestisci contatti in entrata."
            to="/admin/richieste"
            navigate={navigate}
            badge={newContacts}
          />
          <AdminCard
            testid="admin-card-ordini"
            icon={ShoppingCart}
            title="Ordini"
            description="Ordini di aziende e privati: autorizza sconti e segna i pagamenti ricevuti."
            to="/admin/ordini"
            navigate={navigate}
          />
          <AdminCard
            testid="admin-card-pagamenti"
            icon={ReceiptText}
            title="Pagamenti"
            description="Storico dei pagamenti registrati sulla piattaforma."
            to="/admin/pagamenti"
            navigate={navigate}
          />
          <AdminCard
            testid="admin-card-provvigioni"
            icon={Wallet}
            title="Provvigioni"
            description="Maturato dei formatori sui corsi venduti: segna le provvigioni liquidate dopo il pagamento."
            to="/admin/provvigioni"
            navigate={navigate}
          />
        </div>
      </main>
    </div>
  );
}
